export async function loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
        img.src = url;
    });
}

export async function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            blob => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error('Failed to convert canvas to blob'));
                }
            },
            'image/png',
            1.0,
        );
    });
}

export function downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export function drawRoundedImage(
    ctx: CanvasRenderingContext2D,
    image: HTMLImageElement,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
): void {
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(x, y, width, height, radius);
    ctx.clip();

    const scale = Math.max(width / image.width, height / image.height);
    const scaledWidth = image.width * scale;
    const scaledHeight = image.height * scale;
    const offsetX = x + (width - scaledWidth) / 2;
    const offsetY = y + (height - scaledHeight) / 2;

    ctx.drawImage(image, offsetX, offsetY, scaledWidth, scaledHeight);
    ctx.restore();
}

export function drawCircularImage(
    ctx: CanvasRenderingContext2D,
    image: HTMLImageElement,
    x: number,
    y: number,
    size: number,
): void {
    ctx.save();
    ctx.beginPath();
    ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(image, x, y, size, size);
    ctx.restore();
}

export function drawPlaceholderCircle(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number,
    color = 'rgba(255, 255, 255, 0.2)',
): void {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
    ctx.fill();
}

export function drawTruncatedText(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
): void {
    let displayText = text;
    let textWidth = ctx.measureText(displayText).width;

    if (textWidth > maxWidth) {
        while (textWidth > maxWidth && displayText.length > 0) {
            displayText = displayText.slice(0, -1);
            textWidth = ctx.measureText(`${displayText}...`).width;
        }
        displayText = `${displayText}...`;
    }

    ctx.fillText(displayText, x, y);
}

const MAX_GAME_NAME_LENGTH = 80;

export function generateShareFilename(gameName: string, timestamp: Date = new Date()): string {
    let sanitizedGameName = gameName.replace(/[^a-zA-Z0-9\s]/g, '').trim();
    if (sanitizedGameName.length > MAX_GAME_NAME_LENGTH) {
        sanitizedGameName = sanitizedGameName.slice(0, MAX_GAME_NAME_LENGTH).trim();
    }
    const dateStr = timestamp.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
    const timeStr = timestamp.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });

    return `[WAND] ${sanitizedGameName} - ${timeStr} ${dateStr}.png`;
}
